import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type {
  BillingRegionDefault,
  CompanyBillingRegion,
  TargetBillingRegion,
} from '@/lib/types';

/**
 * The billing-region vocabulary the TARGET surfaces share.
 *
 * ⚠️ **THIS MODULE OWNS SLUGS AND ORDER. IT DOES NOT OWN NAMES.** The four
 * region names are columns on `f_billing_region_defaults` that an administrator
 * may rewrite at any time, so they are FETCHED — see
 * {@link useBillingRegionLabels}. `CatalogPage` states the rule at length and
 * this module obeys it rather than restating it: a literal
 * `{united_kingdom: 'United Kingdom', …}` anywhere in `src/` is a second copy of
 * wording the server already owns, correct on the day it is written and
 * silently wrong after the first rename, with nothing failing.
 *
 * ⛔ **So there is deliberately NO `BILLING_REGION_LABELS` constant here.** An
 * earlier draft of the per-region targets work imported one; it does not exist
 * and must not be created. The resolver below is what replaces it.
 */

/**
 * The four bands a COMPANY can be in, in the order a reader meets them.
 *
 * ⚠️ **THIS IS THE MASTER'S `sortOrder`, NOT THE SQL ENUM'S ORDER.** The ENUM
 * is `united_kingdom, europe, rest_of_world, north_america` because
 * `north_america` was appended last by migration 138 and a MySQL ENUM stores the
 * member INDEX — reordering it silently rewrites every row, so it can never be
 * alphabetised. The master's `sortOrder` is 10 / 20 / 25 / 30, which puts North
 * America between Europe and the catch-all. **A picker follows the second; a
 * migration follows the first.** They are different orders answering different
 * questions and neither is the other's tidy version.
 *
 * ⚠️ It is the four bands and NOT the five target choices — see
 * {@link TARGET_BILLING_REGIONS}.
 */
export const BILLING_REGIONS: readonly CompanyBillingRegion[] = [
  'united_kingdom',
  'europe',
  'north_america',
  'rest_of_world',
];

/**
 * The companies nobody has banded — those with no country of record.
 *
 * 🔴 **ON A TARGET THIS IS A STORED VALUE, WHICH IS THE ONE THING
 * `BillingRegionFilter` SAYS IS IMPOSSIBLE OF ITSELF.** That type is a QUERY
 * vocabulary and its own doc says `none` is never stored, never written and
 * never returned — every word of which is still true of a COMPANY's band. A
 * team target inverts it: the unbanded companies are a population somebody
 * commits a number against, so here it is written, stored and read back. The
 * two are separate types on purpose; see {@link TargetBillingRegion}.
 *
 * ⚠️ **NOT the same sentinel as the revenue tab's `NO_REGION_KEY`, which is
 * `(none)` with load-bearing parentheses.** That one shares a URL slot with real
 * ENUM members and needs a spelling no member could ever take. This one is a
 * real value on a real column and is spelled as the server stores it.
 */
export const BILLING_REGION_NONE = 'none' as const;

/**
 * What a TEAM target may name — the four bands **and** the unbanded companies.
 *
 * 🔴 **A PICKER BUILT FROM {@link BILLING_REGIONS} ALONE IS SILENTLY SHORT BY
 * THE ONE BAND THAT IS EASIEST TO FORGET AND HARDEST TO NOTICE.** Every team
 * target must name a region and there is no overall team figure, so a missing
 * fifth option is not a narrower list — it is a population that can never be
 * committed against, and its revenue then counts towards no target at all while
 * the four bands quietly fail to add up to the business.
 */
export const TARGET_BILLING_REGIONS: readonly TargetBillingRegion[] = [
  ...BILLING_REGIONS,
  BILLING_REGION_NONE,
];

/**
 * What the unbanded band is called wherever it is named.
 *
 * ⚠️ **This one IS client wording, and that is not a contradiction of the rule
 * above: the server has no row to own it.** `f_billing_region_defaults` holds
 * one row per real band and nothing for the absence of one, so there is no name
 * to fetch. The string matches the revenue surfaces byte for byte on purpose —
 * one band, one name, wherever a reader meets it.
 */
export const NO_REGION_LABEL = 'No billing region recorded';

/** Resolves a region to what a reader calls it. */
export type BillingRegionLabeller = (region: TargetBillingRegion) => string;

/**
 * The region names, from the master.
 *
 * ⚠️ **A FAILED READ FALLS BACK TO THE RAW SLUG AND REPORTS NOTHING, on
 * purpose, and this is copied from `CatalogPage` rather than invented.** This is
 * a LABEL lookup, never the data: the targets still load, still save and still
 * measure. An error block here would report *"we could not fetch four display
 * names"* as a failure of the thing that worked. The degradation is visible
 * where it matters — `united_kingdom` in place of "United Kingdom" is
 * unmistakable, and is exactly the string somebody needs in order to go and
 * look.
 *
 * ⛔ **Do NOT add a hardcoded fallback map.** That is the silent-after-a-rename
 * failure this whole module is arranged to avoid, and a fallback is where it
 * would come back — correct on the day, and never re-read afterwards.
 *
 * ⚠️ **Each caller runs its own fetch, and that is a duplicated REQUEST rather
 * than duplicated WORDING.** Two fetches cannot disagree about a name; two maps
 * can. The master is four rows and this is the trade `CatalogPage` and
 * `BillingRegionDefaultsCard` already make between them.
 */
export function useBillingRegionLabels(): BillingRegionLabeller {
  const [master, setMaster] = useState<readonly BillingRegionDefault[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listBillingRegionDefaults(controller.signal)
      .then((envelope) => {
        if (controller.signal.aborted) return;
        setMaster(envelope.data);
      })
      .catch(() => {
        /* Deliberately silent — the fallback is the slug. See the doc above. */
      });
    return () => controller.abort();
  }, []);

  return (region: TargetBillingRegion): string => {
    if (region === BILLING_REGION_NONE) return NO_REGION_LABEL;
    return master.find((row) => row.slug === region)?.name ?? region;
  };
}
